import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Nav,
  Row,
} from 'react-bootstrap';
import ResizableTable from '../../common/ResizableTable';
import AuditApi from '../../../api/AuditApi';
import SparkApi from '../../../api/SparkApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { formatDateTime } from '../../../utils/formatUtils';
import './AuditCenter.css';

const RECORDS_PER_PAGE = 20;

function cell(value, displayValue) {
  const v = value ?? '';
  const d = displayValue ?? v;
  return {
    value: v,
    displayValue: d === '' ? '—' : String(d),
  };
}

function formatActor(row) {
  const name = String(row?.actor_name || row?.user_name || '').trim();
  if (name) return name;
  if (row?.actor_email || row?.user_email) {
    return row.actor_email || row.user_email;
  }
  return row?.createdbyid || row?.user_id || '—';
}

function canRestoreAuditLog(row) {
  return row?.can_restore === true || row?.can_restore === 't';
}

function RestoreConfirmModal({ target, show, onHide, onConfirm, restoring, error }) {
  if (!target) return null;

  const label = target.entity_name || 'this list view';
  const objectLabel = target.object_name ? ` on ${target.object_name}` : '';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Restore list view</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="mb-0">
          Restore <span className="fw-semibold">{label}</span>
          {objectLabel}? It will appear again in list view setup for that object.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={restoring}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={restoring}>
          {restoring ? 'Restoring…' : 'Restore'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function FilterBar({ children, onApply, onReset, applying }) {
  return (
    <Card className="border-0 bg-light mb-3">
      <Card.Body className="py-3">
        <Row className="g-2 align-items-end">{children}</Row>
        <div className="d-flex gap-2 mt-3">
          <Button variant="primary" size="sm" onClick={onApply} disabled={applying}>
            Apply filters
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onReset} disabled={applying}>
            Reset
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

const AuditCenter = () => {
  const api = useMemo(() => new AuditApi(), []);
  const [activeTab, setActiveTab] = useState('audit-logs');

  const [meta, setMeta] = useState({ actions: [], entityTypes: [], loginStatuses: [] });
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);

  const [auditRows, setAuditRows] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearch, setAuditSearch] = useState('');
  const [debouncedAuditSearch, setDebouncedAuditSearch] = useState('');
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState('');
  const [auditFilters, setAuditFilters] = useState({
    action: '',
    entityType: '',
    objectName: '',
    userId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [appliedAuditFilters, setAppliedAuditFilters] = useState(auditFilters);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState('');

  const [loginRows, setLoginRows] = useState([]);
  const [loginTotal, setLoginTotal] = useState(0);
  const [loginPage, setLoginPage] = useState(1);
  const [loginSearch, setLoginSearch] = useState('');
  const [debouncedLoginSearch, setDebouncedLoginSearch] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginFilters, setLoginFilters] = useState({
    userId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  const [appliedLoginFilters, setAppliedLoginFilters] = useState(loginFilters);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAuditSearch(auditSearch), 400);
    return () => clearTimeout(timer);
  }, [auditSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLoginSearch(loginSearch), 400);
    return () => clearTimeout(timer);
  }, [loginSearch]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [metaRes, usersRes, objectsRes] = await Promise.all([
          api.fetchMeta(),
          SparkApi.fetchUsers(),
          ObjectConfigApi.fetchObjectConfigs(),
        ]);
        if (metaRes?.data?.success) {
          setMeta(metaRes.data.data || {});
        }
        if (Array.isArray(usersRes)) {
          setUsers(usersRes);
        }
        if (objectsRes?.success && Array.isArray(objectsRes.data)) {
          setObjects(
            objectsRes.data
              .filter((o) => !o.is_deleted)
              .map((o) => ({
                name: o.object_name,
                label: o.object_label || o.object_name,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        }
      } catch (err) {
        console.error('Failed to load audit center metadata', err);
      }
    };
    loadMeta();
  }, [api]);

  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    setAuditError('');
    try {
      const res = await api.fetchLogs({
        page: auditPage,
        limit: RECORDS_PER_PAGE,
        search: debouncedAuditSearch,
        ...appliedAuditFilters,
        dateFrom: appliedAuditFilters.dateFrom
          ? new Date(appliedAuditFilters.dateFrom).toISOString()
          : '',
        dateTo: appliedAuditFilters.dateTo
          ? new Date(`${appliedAuditFilters.dateTo}T23:59:59`).toISOString()
          : '',
      });
      if (res?.data?.success) {
        setAuditRows(res.data.data || []);
        setAuditTotal(res.data.total || 0);
      } else {
        setAuditRows([]);
        setAuditTotal(0);
        setAuditError(res?.data?.message || 'Failed to load audit logs');
      }
    } catch (err) {
      setAuditRows([]);
      setAuditTotal(0);
      setAuditError(err?.response?.data?.message || err.message || 'Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [api, auditPage, debouncedAuditSearch, appliedAuditFilters]);

  const loadLoginHistory = useCallback(async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await api.fetchLoginHistory({
        page: loginPage,
        limit: RECORDS_PER_PAGE,
        search: debouncedLoginSearch,
        ...appliedLoginFilters,
        dateFrom: appliedLoginFilters.dateFrom
          ? new Date(appliedLoginFilters.dateFrom).toISOString()
          : '',
        dateTo: appliedLoginFilters.dateTo
          ? new Date(`${appliedLoginFilters.dateTo}T23:59:59`).toISOString()
          : '',
      });
      if (res?.data?.success) {
        setLoginRows(res.data.data || []);
        setLoginTotal(res.data.total || 0);
      } else {
        setLoginRows([]);
        setLoginTotal(0);
        setLoginError(res?.data?.message || 'Failed to load login history');
      }
    } catch (err) {
      setLoginRows([]);
      setLoginTotal(0);
      setLoginError(err?.response?.data?.message || err.message || 'Failed to load login history');
    } finally {
      setLoginLoading(false);
    }
  }, [api, loginPage, debouncedLoginSearch, appliedLoginFilters]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  useEffect(() => {
    if (activeTab !== 'login-history') {
      return;
    }
    loadLoginHistory();
  }, [activeTab, loadLoginHistory]);

  const auditTableData = useMemo(
    () =>
      auditRows.map((row) => ({
        id: cell(row.id),
        createddate: cell(row.createddate, formatDateTime(row.createddate)),
        action: cell(row.action),
        entity_type: cell(row.entity_type),
        entity_name: cell(row.entity_name),
        object_name: cell(row.object_name),
        actor: cell(row.createdbyid, formatActor(row)),
      })),
    [auditRows]
  );

  const auditColumns = useMemo(
    () => [
      { field: 'action', label: 'Action', sortable: true },
      { field: 'entity_type', label: 'Entity type', sortable: true },
      { field: 'entity_name', label: 'Entity name', sortable: true },
      { field: 'object_name', label: 'Object', sortable: true },
      { field: 'actor', label: 'User', sortable: true },
      { field: 'createddate', label: 'Date', sortable: true },
    ],
    []
  );

  const loginTableData = useMemo(
    () =>
      loginRows.map((row) => ({
        id: cell(row.id),
        login_datetime: cell(
          row.login_datetime || row.createddate,
          formatDateTime(row.login_datetime || row.createddate)
        ),
        logout_datetime: cell(row.logout_datetime, formatDateTime(row.logout_datetime)),
        status: cell(row.status),
        user: cell(row.user_id, formatActor(row)),
        ip_address: cell(row.ip_address),
        user_agent: cell(
          row.user_agent,
          row.user_agent
            ? String(row.user_agent).length > 80
              ? `${String(row.user_agent).slice(0, 80)}…`
              : row.user_agent
            : '—'
        ),
        error_message: cell(row.error_message),
      })),
    [loginRows]
  );

  const loginColumns = useMemo(
    () => [
      { field: 'login_datetime', label: 'Login', sortable: true },
      { field: 'logout_datetime', label: 'Logout', sortable: true },
      { field: 'status', label: 'Status', sortable: true },
      { field: 'user', label: 'User', sortable: true },
      { field: 'ip_address', label: 'IP address', sortable: true },
      { field: 'user_agent', label: 'User agent', sortable: true },
      { field: 'error_message', label: 'Error', sortable: true },
    ],
    []
  );

  const handleRestoreConfirm = async () => {
    if (!restoreTarget?.id) return;
    setRestoring(true);
    setRestoreError('');
    try {
      const res = await api.restoreFromAuditLog(restoreTarget.id);
      if (res?.data?.success) {
        setRestoreTarget(null);
        setRestoreSuccess('List view restored successfully.');
        loadAuditLogs();
      } else {
        setRestoreError(res?.data?.message || 'Failed to restore list view');
      }
    } catch (err) {
      setRestoreError(err?.response?.data?.message || err.message || 'Failed to restore list view');
    } finally {
      setRestoring(false);
    }
  };

  const auditActionsRenderer = (record) => {
    const id = record?.id?.value;
    if (!id) return null;
    const row = auditRows.find((r) => r.id === id);
    if (!canRestoreAuditLog(row)) return null;
    return (
      <Button
        size="sm"
        variant="outline-primary"
        onClick={(e) => {
          e.stopPropagation();
          setRestoreError('');
          setRestoreTarget({
            id,
            entity_name: row.entity_name,
            object_name: row.object_name,
          });
        }}
      >
        Restore
      </Button>
    );
  };

  const resetAuditFilters = () => {
    const empty = {
      action: '',
      entityType: '',
      objectName: '',
      userId: '',
      dateFrom: '',
      dateTo: '',
    };
    setAuditFilters(empty);
    setAppliedAuditFilters(empty);
    setAuditSearch('');
    setDebouncedAuditSearch('');
    setAuditPage(1);
  };

  const resetLoginFilters = () => {
    const empty = { userId: '', status: '', dateFrom: '', dateTo: '' };
    setLoginFilters(empty);
    setAppliedLoginFilters(empty);
    setLoginSearch('');
    setDebouncedLoginSearch('');
    setLoginPage(1);
  };

  return (
    <Container fluid className="mt-4 px-4 pb-4">
      <Row className="mb-3">
        <Col>
          <h4 className="fw-semibold mb-1">Audit Center</h4>
          <p className="text-muted small mb-0">
            Review setup changes and user login activity for your organization.
          </p>
        </Col>
      </Row>

      <Nav variant="tabs" className="mb-3 audit-center-tabs">
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'audit-logs'}
            onClick={() => setActiveTab('audit-logs')}
            style={{ cursor: 'pointer' }}
          >
            Setup Audit
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'login-history'}
            onClick={() => setActiveTab('login-history')}
            style={{ cursor: 'pointer' }}
          >
            Login History
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === 'audit-logs' && (
        <>
            <FilterBar
              onApply={() => {
                setAppliedAuditFilters({ ...auditFilters });
                setAuditPage(1);
              }}
              onReset={resetAuditFilters}
              applying={auditLoading}
            >
              <Col md={2}>
                <Form.Label className="small mb-1">Action</Form.Label>
                <Form.Select
                  size="sm"
                  value={auditFilters.action}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, action: e.target.value }))}
                >
                  <option value="">All</option>
                  {(meta.actions || []).map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Entity type</Form.Label>
                <Form.Select
                  size="sm"
                  value={auditFilters.entityType}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, entityType: e.target.value }))}
                >
                  <option value="">All</option>
                  {(meta.entityTypes || []).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Object</Form.Label>
                <Form.Select
                  size="sm"
                  value={auditFilters.objectName}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, objectName: e.target.value }))}
                >
                  <option value="">All</option>
                  {objects.map((o) => (
                    <option key={o.name} value={o.name}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">User</Form.Label>
                <Form.Select
                  size="sm"
                  value={auditFilters.userId}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, userId: e.target.value }))}
                >
                  <option value="">All</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u.id}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={auditFilters.dateFrom}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={auditFilters.dateTo}
                  onChange={(e) => setAuditFilters((f) => ({ ...f, dateTo: e.target.value }))}
                />
              </Col>
            </FilterBar>

            {auditError && <Alert variant="danger">{auditError}</Alert>}
            {restoreSuccess && (
              <Alert
                variant="success"
                dismissible
                onClose={() => setRestoreSuccess('')}
              >
                {restoreSuccess}
              </Alert>
            )}

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <ResizableTable
                  title="Setup Audit Log"
                  data={auditTableData}
                  columns={auditColumns}
                  loading={auditLoading}
                  searchTerm={auditSearch}
                  onSearchChange={(value) => {
                    setAuditSearch(value);
                    setAuditPage(1);
                  }}
                  currentPage={auditPage}
                  totalRecords={auditTotal}
                  recordsPerPage={RECORDS_PER_PAGE}
                  onPageChange={setAuditPage}
                  showActions
                  actionsRenderer={auditActionsRenderer}
                  showNewButton={false}
                  showPagination={auditTotal > RECORDS_PER_PAGE}
                  searchPlaceholder="Search entity, object, action…"
                  emptyMessage="No audit log entries found."
                  onRefresh={loadAuditLogs}
                  exportFilename="audit_log"
                />
              </Card.Body>
            </Card>
        </>
      )}

      {activeTab === 'login-history' && (
        <>
            <FilterBar
              onApply={() => {
                setAppliedLoginFilters({ ...loginFilters });
                setLoginPage(1);
              }}
              onReset={resetLoginFilters}
              applying={loginLoading}
            >
              <Col md={3}>
                <Form.Label className="small mb-1">User</Form.Label>
                <Form.Select
                  size="sm"
                  value={loginFilters.userId}
                  onChange={(e) => setLoginFilters((f) => ({ ...f, userId: e.target.value }))}
                >
                  <option value="">All</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u.id}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={loginFilters.status}
                  onChange={(e) => setLoginFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="">All</option>
                  {(meta.loginStatuses || []).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={loginFilters.dateFrom}
                  onChange={(e) => setLoginFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={loginFilters.dateTo}
                  onChange={(e) => setLoginFilters((f) => ({ ...f, dateTo: e.target.value }))}
                />
              </Col>
            </FilterBar>

            {loginError && <Alert variant="danger">{loginError}</Alert>}

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <ResizableTable
                  title="Login History"
                  data={loginTableData}
                  columns={loginColumns}
                  loading={loginLoading}
                  searchTerm={loginSearch}
                  onSearchChange={(value) => {
                    setLoginSearch(value);
                    setLoginPage(1);
                  }}
                  currentPage={loginPage}
                  totalRecords={loginTotal}
                  recordsPerPage={RECORDS_PER_PAGE}
                  onPageChange={setLoginPage}
                  showActions={false}
                  showNewButton={false}
                  showPagination={loginTotal > RECORDS_PER_PAGE}
                  searchPlaceholder="Search IP, user agent, status…"
                  emptyMessage="No login history found."
                  onRefresh={loadLoginHistory}
                  exportFilename="login_history"
                />
              </Card.Body>
            </Card>
        </>
      )}

      <RestoreConfirmModal
        target={restoreTarget}
        show={Boolean(restoreTarget)}
        onHide={() => {
          if (!restoring) {
            setRestoreTarget(null);
            setRestoreError('');
          }
        }}
        onConfirm={handleRestoreConfirm}
        restoring={restoring}
        error={restoreError}
      />
    </Container>
  );
};

export default AuditCenter;
